import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { AuthProvider, useAuth } from './lib/auth-context';
import { bootstrapTheme } from './lib/theme';
import { Toaster } from './components/ui/sonner';
import ErrorBoundary from './components/ErrorBoundary';
import { installGlobalErrorReporting } from './lib/error-reporting';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import RefersPage from './pages/RefersPage';
import ReferLandingPage from './pages/ReferLandingPage';
import LearnerSettingsPage from './pages/LearnerSettingsPage';
// V14.5 Part 19 — Baby Tracker page deprecated on web. The feature is now
// mobile-app-exclusive (per founder direction). Route removed below.
import AuthCallback from './pages/AuthCallback';
import GoogleRegister from './pages/GoogleRegister';
import AdminLayout from './layouts/AdminLayout';
import InstructorLayout from './layouts/InstructorLayout';
import LearnerLayout from './layouts/LearnerLayout';
import AdminDashboard from './pages/admin/Dashboard';
import BatchList from './pages/admin/BatchList';
import BatchDetail from './pages/admin/BatchDetail';
import ArchivedBatches from './pages/admin/ArchivedBatches';
import CreateBatch from './pages/admin/CreateBatch';
import ScheduleSessions from './pages/admin/ScheduleSessions';
import EditSession from './pages/admin/EditSession';
import CalendarPage from './pages/admin/CalendarPage';
import UserManagement from './pages/admin/UserManagement';
import SettingsPage from './pages/admin/SettingsPage';
import LaunchReadinessPage from './pages/admin/LaunchReadinessPage';
import TestPushPage from './pages/admin/TestPushPage';
import AvailabilityPage from './pages/admin/AvailabilityPage';
import ZoomManagement from './pages/admin/ZoomManagement';
import AuditPage from './pages/admin/AuditPage';
import ReassignmentQueuePage from './pages/admin/ReassignmentQueuePage';
import FinanceDashboard from './pages/admin/FinanceDashboard';
import SoulAIAdmin from './pages/admin/SoulAIAdmin';
import PrenatalVideosAdmin from './pages/admin/PrenatalVideosAdmin';
import PrenatalCoach from './pages/learner/PrenatalCoach';
import LeaveManagement from './pages/admin/LeaveManagement';
import HolidaysPage from './pages/admin/HolidaysPage';
import InstructorDashboard from './pages/instructor/Dashboard';
import InstructorBatches from './pages/instructor/MyBatches';
import InstructorSchedule from './pages/instructor/MySchedule';
import InstructorPayouts from './pages/instructor/Payouts';
import LearnerDashboard from './pages/learner/Dashboard';
import LearnTab from './pages/learner/LearnTab';
import SSOLanding from './pages/SSOLanding';
import AdminAccessConsole from './pages/admin/AdminAccessConsole';
import LearnerBatches from './pages/learner/MyBatches';
import TrimesterSelect from './pages/learner/TrimesterSelect';
import TrialClasses from './pages/learner/TrialClasses';
import MedicalVault from './pages/learner/MedicalVault';
import RecordedCourses from './pages/learner/RecordedCourses';
import MyBillingPage from './pages/learner/MyBillingPage';
import BookClasses from './pages/learner/BookClasses';
import PublicJoin from './pages/PublicJoin';
import PublicHost from './pages/PublicHost';
import DataMigration from './pages/admin/DataMigration';
import AnalyticsDashboard from './pages/admin/AnalyticsDashboard';
import BannerAdmin from './pages/admin/BannerAdmin';
import DailyTipsAdmin from './pages/admin/DailyTipsAdmin';
import StorageDashboard from './pages/admin/StorageDashboard';
import CourseList from './pages/admin/CourseList';
import CreateCourse from './pages/admin/CreateCourse';
import CourseDetail from './pages/admin/CourseDetail';
import BundleManager from './pages/admin/BundleManager';
import LearnCoursePlayer from './pages/learner/LearnCoursePlayer';
import ReferAFriend from './pages/learner/ReferAFriend';
import ProfilePage from './pages/ProfilePage';
import DoctorLayout from './layouts/DoctorLayout';
import DoctorDashboard from './pages/doctor/Dashboard';
import DoctorChat from './pages/doctor/Chat';
import DoctorPortal from './pages/doctor/DoctorPortal';
import DoctorInbox from './pages/doctor/DoctorInbox';
import NutritionistInbox from './pages/doctor/NutritionistInbox';
import Nutritionist from './pages/learner/Nutritionist';
import DietPaymentPage from './pages/learner/DietPaymentPage';
import EliteProgram from './pages/learner/EliteProgram';
import PostnatalOnboardingModal from './components/PostnatalOnboardingModal';
import './App.css';

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/login" />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  // V14.1 — Bootstrap theme (Light/Dark/System) on every mount. Idempotent.
  // Done here (not at module-load) so it re-applies after auth state changes
  // and survives the loading splash → main view transition.
  useEffect(() => { bootstrapTheme(); }, []);

  // V14.5 Part 16 — Install global window.onerror + unhandledrejection
  // listeners that POST to /api/client-errors. Catches errors OUTSIDE the
  // React render tree (async handlers, promise rejections, lazy-chunk
  // load failures) — the ErrorBoundary alone can't see those.
  useEffect(() => { installGlobalErrorReporting(); }, []);

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  const getDefaultRoute = () => {
    if (!user) return '/login';
    if (user.role === 'superadmin' || user.role === 'admin') return '/admin/dashboard';
    if (user.role === 'instructor') return '/instructor/dashboard';
    if (user.role === 'doctor') return user.medical_category === 'nutritionist' ? '/doctor/medical-inbox' : '/doctor/dashboard';
    if (user.role === 'learner') {
      return '/learner/dashboard';
    }
    return '/login';
  };

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/register" element={<GoogleRegister />} />
      <Route path="/join/:slug" element={<PublicJoin />} />
      <Route path="/host/:slug" element={<PublicHost />} />
      <Route path="/sso" element={<SSOLanding />} />

      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute roles={['superadmin', 'admin']}><AdminLayout /></ProtectedRoute>}>
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="batches" element={<BatchList />} />
        <Route path="batches/archived" element={<ArchivedBatches />} />
        <Route path="batches/create" element={<CreateBatch />} />
        <Route path="batches/:batchId" element={<BatchDetail />} />
        <Route path="batches/:batchId/schedule" element={<ScheduleSessions />} />
        <Route path="batches/:batchId/edit-session" element={<EditSession />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="availability" element={<AvailabilityPage />} />
        <Route path="zoom" element={<ZoomManagement />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="reassignments" element={<ReassignmentQueuePage />} />
        <Route path="finance" element={<FinanceDashboard />} />
        <Route path="soulai" element={<SoulAIAdmin />} />
        <Route path="prenatal-coach" element={<PrenatalVideosAdmin />} />
        <Route path="analytics" element={<AnalyticsDashboard />} />
        <Route path="analytics-audit" element={<Navigate to="/admin/audit" replace />} />
        <Route path="leaves" element={<LeaveManagement />} />
        <Route path="holidays" element={<HolidaysPage />} />
        <Route path="migration" element={<DataMigration />} />
        <Route path="banners" element={<BannerAdmin />} />
        <Route path="daily-tips" element={<DailyTipsAdmin />} />
        <Route path="storage" element={<StorageDashboard />} />
        <Route path="courses" element={<CourseList />} />
        <Route path="courses/create" element={<CreateCourse />} />
        <Route path="courses/:courseId" element={<CourseDetail />} />
        <Route path="bundles" element={<BundleManager />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="launch-readiness" element={<LaunchReadinessPage />} />
        <Route path="test-push" element={<TestPushPage />} />
        <Route path="medical-team" element={<Navigate to="/admin/users" replace />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="access/user/:userId" element={<AdminAccessConsole />} />
      </Route>

      {/* Instructor Routes */}
      <Route path="/instructor" element={<ProtectedRoute roles={['instructor']}><InstructorLayout /></ProtectedRoute>}>
        <Route path="dashboard" element={<InstructorDashboard />} />
        <Route path="batches" element={<InstructorBatches />} />
        <Route path="schedule" element={<InstructorSchedule />} />
        <Route path="availability" element={<AvailabilityPage />} />
        <Route path="leaves" element={<LeaveManagement />} />
        <Route path="payouts" element={<InstructorPayouts />} />
        <Route path="profile" element={<ProfilePage />} />
      </Route>

      {/* Learner Routes */}
      <Route path="/learner" element={<ProtectedRoute roles={['learner']}><LearnerLayout /></ProtectedRoute>}>
        <Route path="dashboard" element={<LearnerDashboard />} />
        <Route path="learn" element={<LearnTab />} />
        <Route path="learn/course/:courseId" element={<LearnCoursePlayer />} />
        <Route path="refer" element={<ReferAFriend />} />
        <Route path="book" element={<BookClasses />} />
        <Route path="trial-classes" element={<TrialClasses />} />
        <Route path="elite" element={<EliteProgram />} />
        <Route path="batches" element={<LearnerBatches />} />
        <Route path="vault" element={<MedicalVault />} />
        <Route path="nutritionist" element={<Nutritionist />} />
        <Route path="diet-payment/:rid" element={<DietPaymentPage />} />
        <Route path="courses" element={<Navigate to="/learner/learn" replace />} />
        <Route path="courses/:courseId" element={<RecordedCourses />} />
        <Route path="trimester-select" element={<TrimesterSelect />} />
        <Route path="soulai-coach" element={<PrenatalCoach />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="refers" element={<RefersPage />} />
        <Route path="settings" element={<LearnerSettingsPage />} />
        <Route path="billing" element={<MyBillingPage />} />
        {/* V14.5 Part 19 — baby-tracker removed from web; mobile-app-only */}
      </Route>

      {/* Public referral landing — anyone can hit this */}
      <Route path="/refer/:code" element={<ReferLandingPage />} />

      {/* Doctor Routes */}
      <Route path="/doctor" element={<ProtectedRoute roles={['doctor']}><DoctorLayout /></ProtectedRoute>}>
        <Route path="dashboard" element={<DoctorPortal />} />
        <Route path="medical-inbox" element={<NutritionistInbox />} />
        <Route path="inbox" element={<DoctorInbox />} />
        <Route path="queue" element={<DoctorDashboard />} />
        <Route path="consultations" element={<DoctorDashboard />} />
        <Route path="chat/:sessionId" element={<DoctorChat />} />
        <Route path="availability" element={<AvailabilityPage />} />
        <Route path="referrals" element={<DoctorDashboard />} />
      </Route>

      <Route path="/" element={<Navigate to={getDefaultRoute()} />} />
      <Route path="*" element={<Navigate to={getDefaultRoute()} />} />
    </Routes>
  );
}

function GlobalLearnerModals() {
  // V13.14.7 — mount mandatory postnatal onboarding modal globally so any
  // MTTF-migrated learner sees it on their first login regardless of route.
  const { user } = useAuth();
  const isLearner = user?.role === 'learner';
  const needsPostnatalOnboarding = !!(isLearner && user?.pending_postnatal_onboarding);
  if (!needsPostnatalOnboarding) return null;
  return <PostnatalOnboardingModal open />;
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <GlobalLearnerModals />
          <Toaster position="top-right" richColors duration={3000} />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
